import { defineStore } from 'pinia'
import { getMsgBroadcaster } from '@shared/wallet'
import { Wallet, isCosmosWallet } from '@injectivelabs/wallet-base'
import { DEFAULT_BLOCK_TIMEOUT_HEIGHT } from '@injectivelabs/utils'
import { injNameService, injBonfidaNameService } from '@shared/service'
import {
  ErrorType,
  WalletException,
  UnspecifiedErrorCode,
  ChainCosmosErrorCode,
  CosmosWalletException
} from '@injectivelabs/exceptions'
import { blacklistedAddresses } from '@/app/json'
import { EventBus } from '@/types'

type WalletStoreState = {}

const initialStateFactory = (): WalletStoreState => ({})

export const useWalletStore = defineStore('wallet', {
  state: (): WalletStoreState => initialStateFactory(),
  actions: {
    async connect({
      wallet,
      address,
      derivationPath
    }: {
      wallet: Wallet
      address?: string
      derivationPath?: string
    }) {
      const walletStore = useWalletStore()
      const sharedWalletStore = useSharedWalletStore()

      if (
        (
          [
            Wallet.Rabby,
            Wallet.BitGet,
            Wallet.Rainbow,
            Wallet.Phantom,
            Wallet.Metamask,
            Wallet.OkxWallet,
            Wallet.TrustWallet
          ] as Wallet[]
        ).includes(wallet)
      ) {
        await sharedWalletStore.connectEvmWallet(wallet)
      } else if (([Wallet.Leap, Wallet.Keplr] as Wallet[]).includes(wallet)) {
        await sharedWalletStore.connectCosmosWallet(wallet)
      } else if (wallet === Wallet.WalletConnect) {
        await sharedWalletStore.connectWalletConnect()
        const msgBroadcaster = await getMsgBroadcaster()
        await msgBroadcaster.setOptions({
          txTimeout: DEFAULT_BLOCK_TIMEOUT_HEIGHT * 5
        })
      } else if (
        ([Wallet.TrezorBip32, Wallet.TrezorBip44] as Wallet[]).includes(
          wallet
        ) &&
        address
      ) {
        await sharedWalletStore.connectTrezor({
          wallet,
          address,
          derivationPath
        })
      } else if (wallet === Wallet.Ledger && address) {
        await sharedWalletStore.connectLedger({
          wallet,
          address,
          derivationPath
        })
      } else if (wallet === Wallet.LedgerCosmos && address) {
        await sharedWalletStore.connectLedgerCosmos(address, derivationPath)
      } else {
        throw new Error(`${wallet} not supported.`)
      }

      // temp - honeyPot
      const tempNonBlacklistedAddresses = [
        '0x7bc5cb059f21553af489d2b2DF3d40aaaE9b44e8',
        '0x430aB3C698b3210548b6AC9f72936B43B15EBE9B',
        '0xb9436d76e8fe08859d042e41b4a21c85715e1176'
      ]

      if (sharedWalletStore.isUserConnected) {
        const someAddressInWalletIsBlackListed =
          sharedWalletStore.addresses.some((address) =>
            blacklistedAddresses.find((blacklistedAddress) => {
              const isPartOfHoneyPotList = tempNonBlacklistedAddresses.find(
                (tempAddress) =>
                  tempAddress.toLowerCase() === address.toLowerCase()
              )

              return (
                !isPartOfHoneyPotList &&
                blacklistedAddress.toLowerCase() === address.toLowerCase()
              )
            })
          )

        if (someAddressInWalletIsBlackListed) {
          walletStore.disconnect()

          throw new WalletException(
            new Error('Connected account address is restricted.'),
            {
              code: UnspecifiedErrorCode,
              type: ErrorType.WalletError
            }
          )
        }
      }

      await walletStore.init()
    },

    async init() {
      const bankStore = useBankStore()
      const accountStore = useAccountStore()
      const sharedWalletStore = useSharedWalletStore()

      await sharedWalletStore.init()

      if (!sharedWalletStore.isUserConnected) {
        return
      }

      accountStore.reset()

      await accountStore.getWalletNetworkAddresses(sharedWalletStore.wallet)

      // make sure network addresses is fetched before balance
      await bankStore.fetchBankBalance()
    },

    async validateAndQueue() {
      const bankStore = useBankStore()
      const sharedWalletStore = useSharedWalletStore()

      if (
        isCosmosWallet(sharedWalletStore.wallet) &&
        !bankStore.hasEnoughInjForGas
      ) {
        throw new CosmosWalletException(
          new Error('Insufficient INJ to pay for gas/transaction fees.'),
          {
            code: UnspecifiedErrorCode,
            type: ErrorType.WalletError,
            contextCode: ChainCosmosErrorCode.ErrInsufficientFee
          }
        )
      }

      await sharedWalletStore.validateAndQueue()
    },

    async disconnect() {
      const bankStore = useBankStore()
      const tokenStore = useTokenStore()
      const accountStore = useAccountStore()
      const transactionStore = useTransactionStore()
      const sharedWalletStore = useSharedWalletStore()
      const pendingTransactionStore = usePendingTransactionStore()

      if (sharedWalletStore.wallet === Wallet.WalletConnect) {
        const msgBroadcaster = await getMsgBroadcaster()
        await msgBroadcaster.setOptions({
          txTimeout: DEFAULT_BLOCK_TIMEOUT_HEIGHT
        })
      }

      useEventBus(EventBus.DisconnectWallet).emit()

      await sharedWalletStore.logout()

      bankStore.reset()
      tokenStore.reset()
      accountStore.reset()
      transactionStore.reset()
      pendingTransactionStore.reset()
    },

    fetchInjAddressFromNameService(name: string): Promise<string> {
      return injNameService.fetchInjAddress(name.trim().toLowerCase())
    },

    fetchInjAddressFromBonfidaNameService(name: string): Promise<string> {
      return injBonfidaNameService.fetchInjAddress(name.trim().toLowerCase())
    }
  }
})
